import { useCallback, useRef } from 'react';
import { placeCaretAfterAtomic } from '../../../DocEditor/utils/atomicCaret';
import { hasMathSlash } from '../../mathSlash';
import { useFieldMathMenu } from '../../useFieldMathMenu';
import {
  createZwspMarker,
  deleteCharsBeforeCaret,
  menuPositionFromMarker,
} from '../utils/caret';
import { nestedHtmlToValue, paintMathPill } from '../utils/nestedHtml';

/** `/math` trigger + formula menu for live math pills in nested fields. */
export const useNestedMathInsert = ({
  elRef,
  readOnly,
  resolveVariable,
  pickerVariables,
  onCreateVariable,
  lastCommittedRef,
  onCommit,
  readValue = null,
}) => {
  const editingMathRef = useRef(null);

  const deleteMathTrigger = useCallback(() => {
    const el = elRef.current;
    if (!el) return false;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return false;
    const range = sel.getRangeAt(0);
    const node = range.startContainer;
    if (node.nodeType !== Node.TEXT_NODE) return false;
    const offset = range.startOffset;
    const text = node.textContent || '';
    const before = text.slice(0, offset);
    if (!hasMathSlash(before)) return false;
    const cut = before.toLowerCase().endsWith('/math') ? 5 : 0;
    if (!cut) return false;
    return deleteCharsBeforeCaret(cut);
  }, [elRef]);

  const insertMathExpr = useCallback(
    (expr) => {
      const el = elRef.current;
      if (!el || readOnly) return;
      el.focus();
      const existing = editingMathRef.current;
      editingMathRef.current = null;
      if (existing && el.contains(existing)) {
        paintMathPill(existing, expr, resolveVariable);
      } else {
        const span = paintMathPill(document.createElement('span'), expr, resolveVariable);
        const marker = el.querySelector('[data-math-insert-mark]');
        if (marker) {
          marker.parentNode.insertBefore(span, marker);
          marker.remove();
        } else {
          el.appendChild(span);
        }
        if (!span.nextSibling || span.nextSibling.nodeType !== Node.TEXT_NODE) {
          span.after(document.createTextNode('\u200B'));
        }
        placeCaretAfterAtomic(span);
      }
      const next = readValue ? readValue() : nestedHtmlToValue(el);
      if (next !== lastCommittedRef.current) {
        lastCommittedRef.current = next;
        onCommit?.(next, { silent: false });
      }
    },
    [elRef, lastCommittedRef, onCommit, readOnly, readValue, resolveVariable]
  );

  const { mathMenu, openMathMenu, mathMenuOpenRef } = useFieldMathMenu({
    resolveVariable,
    availableVariables: pickerVariables,
    onCreateVariable,
    onInsertExpr: insertMathExpr,
    onRestoreFocus: () => {
      editingMathRef.current = null;
      elRef.current?.querySelector('[data-math-insert-mark]')?.remove();
      elRef.current?.focus();
    },
  });

  const openMathAtCaret = useCallback(() => {
    const el = elRef.current;
    if (!el || readOnly || mathMenuOpenRef.current) return;
    deleteMathTrigger();
    el.querySelectorAll('[data-math-insert-mark]').forEach((n) => n.remove());
    const marker = createZwspMarker('mathInsertMark');
    const sel = window.getSelection();
    try {
      if (sel && sel.rangeCount > 0 && el.contains(sel.anchorNode)) {
        const range = sel.getRangeAt(0);
        range.collapse(true);
        range.insertNode(marker);
      } else {
        el.appendChild(marker);
      }
    } catch {
      el.appendChild(marker);
    }
    openMathMenu(menuPositionFromMarker(marker, el));
  }, [deleteMathTrigger, elRef, mathMenuOpenRef, openMathMenu, readOnly]);

  const openMathOnPill = useCallback(
    (pill) => {
      editingMathRef.current = pill;
      const rect = pill.getBoundingClientRect();
      openMathMenu(
        { x: rect.left, y: rect.bottom + 4, caretTop: rect.top },
        pill.getAttribute('data-math-expr') || ''
      );
    },
    [openMathMenu]
  );

  return {
    mathMenu,
    mathMenuOpenRef,
    openMathAtCaret,
    openMathOnPill,
  };
};
